"use client";

import { createHash } from "crypto";

import { sendGTMEvent } from "@next/third-parties/google";
import { usePathname, useRouter } from "next/navigation";
import React, { useEffect, useMemo } from "react";
import * as Sentry from "@sentry/nextjs";

import config from "../services/config";
import { logout } from "../ssr/services/actions";

interface AppUserType {
  token: string;
  email: string | undefined;
  uid: string;
  roles: string[];
  providers: string[];
  session: "PROVIDERS" | "DEEP_LINK"
}

interface Props {
  user?: AppUserType | null;
  children: React.ReactNode;
}

interface AuthContextType {
  user?: AppUserType;
  login: (reason?: string, path?:string, back?: string) => void;
  signup: (back?: string) => void;
  logout: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextType>({
  user: undefined,
  login: () => undefined,
  signup: () => undefined,
  logout: () => Promise.resolve(),
});

const AuthProvider: React.FC<Props> = ({ user, children }) => {
  const pathname = usePathname();
  const router = useRouter();
  const getAuthCookie = () => {
    const cookies = document.cookie?.split("; ") || [];
    const authCookie = cookies.find((c) => c.startsWith("auth-session="));
    return authCookie?.replace("auth-session=", "") || undefined;
  };

  const login = (reason?: string, path?:string, back?: string) => {
    const host = `${window.location.protocol}//${window.location.host}`;
    const redirectUrl = (() => {
      if (path && path.startsWith(host) && !path.includes("/login")) return path;
      const r = `${host}${pathname}`;
      if (r.includes("/login")) return host;
      return r;
    })();
    window.location.href = `${config.app.login}/login?client_id=${config.app.clientId}&redirect_url=${redirectUrl}&back=${host}${back ?? ""}${reason ? `&reason=${reason}` : ""}`;
  };

  const signup = (back?: string) => {
    const host = `${window.location.protocol}//${window.location.host}`;
    window.location.href = `${config.app.login}/signup?client_id=${config.app.clientId}&redirect_url=${host}${pathname}&back=${host}${back ?? ""}`;
  };

  useEffect(() => {
    if (user && !getAuthCookie() && !pathname.includes("/login")) {
      logout();
      login("EXPIRED_SESSION");
    }
  }, [pathname, user]);

  useEffect(() => {
    if (user) {
      sendGTMEvent({ user_id: user.uid });
      sendGTMEvent({ user_data: { sha256_email_address: createHash("sha256").update(user.email).digest("hex") } });
      try {
        if (window?.clarity) window.clarity("set", "userUid", user.uid);
      } catch (error) { /* */ }
    } else {
      sendGTMEvent({ user_id: null });
      sendGTMEvent({ user_data: null });
    }
  }, [user]);

  useEffect(() => {
    // notify sentry
    if (user) {
      Sentry.setUser({ id: user.uid });
    } else {
      Sentry.setUser(null);
    }
  }, [user?.uid]);

  const defaultValue = useMemo(
    () => ({
      user: user || undefined,
      login,
      signup,
      logout: () => logout().then(() => router.push("/")),
    }),
    [config, pathname, user],
  );

  return (
    <AuthContext.Provider value={defaultValue}>
      {user?.session === "DEEP_LINK" && <div className="bg-red-600 text-center text-white">Impersonate session</div>}
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => React.useContext(AuthContext);

export default AuthContext;
export { AuthProvider, useAuth };
export type { AppUserType };
